import { useState } from 'react'
import { cn } from '@/lib/cn'
import { parseVideo } from '@/lib/video'

interface VideoEmbedProps {
  url: string
  /** Names the video for screen readers and the player frame. */
  title: string
  className?: string
  /** Start with the player loaded and playing, e.g. in the gallery viewer. */
  autoPlay?: boolean
}

/**
 * A YouTube or Vimeo video. Until it's played it's only a still and a play
 * button: the provider's player (and its cookies and scripts) loads on click,
 * so a page with videos stays quick and private for people who don't watch.
 */
export function VideoEmbed({ url, title, className, autoPlay = false }: VideoEmbedProps) {
  const [playing, setPlaying] = useState(autoPlay)
  const video = parseVideo(url)
  if (!video) return null

  return (
    <div className={cn('video-embed', className)}>
      {playing ? (
        <iframe
          src={video.embedUrl}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      ) : (
        <button
          type="button"
          className="video-embed-facade"
          onClick={(event) => {
            event.stopPropagation()
            setPlaying(true)
          }}
          aria-label={`Play video: ${title}`}
        >
          {video.thumbnail ? (
            <img src={video.thumbnail} alt="" loading="lazy" />
          ) : (
            <span className="video-embed-provider">
              {video.provider === 'vimeo' ? 'Vimeo' : 'Video'}
            </span>
          )}
          <span className="video-embed-play" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M8 5.5v13l11-6.5z" />
            </svg>
          </span>
        </button>
      )}
    </div>
  )
}
